import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { Plus, Edit, Trash2, Save } from 'lucide-react';
import {
  AppButton, AppModal, StatusBadge, IconButton, AppDataTable, sortRows,
} from '../ui';
import CommonToaster from '../Common/CommonToaster';
import {
  listCourseCategories,
  createCourseCategory,
  updateCourseCategory,
  deleteCourseCategory,
} from '../../utils/courseCategoryApi';

const emptyRow = { label: '', isActive: true };

/**
 * Manage institute course categories (CMS filters + course forms). Slugs are generated on the server from the name.
 */
export default function CourseCategoriesModal({ show, onHide, canManage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [sort, setSort] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listCourseCategories();
      setItems(data?.data || []);
    } catch {
      showToast('Failed to load categories', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const openCreate = () => setEditing({ ...emptyRow });
  const openEdit = (row) =>
    setEditing({
      _id: row._id,
      label: row.label || '',
      isActive: row.isActive !== false,
    });
  const close = () => setEditing(null);

  const columns = useMemo(() => [
    {
      key: 'label',
      label: 'Name',
      sortable: true,
      sortValue: (row) => row.label || '',
      render: (row) => <span className="tw-font-medium tw-text-content">{row.label}</span>,
    },
    {
      key: 'isActive',
      label: 'On website',
      sortable: true,
      // The BOOLEAN, so the two states group; the badge below reads the same
      // answer as a word.
      sortValue: (row) => row.isActive !== false,
      render: (row) => (
        <StatusBadge status={row.isActive !== false ? 'success' : 'neutral'}>
          {row.isActive !== false ? 'Yes' : 'No'}
        </StatusBadge>
      ),
    },
    canManage && {
      key: 'actions',
      label: '',
      align: 'right',
      width: 88,
      pinned: 'right',
      resizable: false,
      // ⚠️ `IconButton`, not `AppButton` with an `aria-label` — same underlying
      // button, but the accessible name becomes a required prop rather than
      // something the next edit can drop. The names are per row, so two
      // categories never announce as the same control.
      render: (row) => (
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
          <IconButton size="sm" label={`Edit ${row.label}`} icon={Edit} onClick={() => openEdit(row)} />
          <IconButton
            size="sm"
            variant="ghost-destructive"
            label={`Delete ${row.label}`}
            icon={Trash2}
            onClick={() => setDeleteId(row._id)}
          />
        </div>
      ),
    },
    // `openEdit` closes only over `setEditing`, which React keeps stable.
  ].filter(Boolean), [canManage]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => sortRows(items, columns, sort), [items, columns, sort]);

  const save = async () => {
    if (!editing?.label?.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const body = {
        label: editing.label.trim(),
        isActive: !!editing.isActive,
      };
      if (editing._id) {
        await updateCourseCategory(editing._id, body);
        showToast('Category updated');
      } else {
        await createCourseCategory(body);
        showToast('Category added');
      }
      close();
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteCourseCategory(deleteId);
      showToast('Category removed');
      setDeleteId(null);
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Delete failed', 'error');
      setDeleteId(null);
    }
  };

  return (
    <>
      <AppModal show={show} title="Course categories" onHide={onHide} size="lg" bodyClassName="">
        <p className="text-muted small mb-3">
          Names appear as filters on your public website and in course dropdowns. Inactive categories stay hidden from the site but courses keep their assignment.
        </p>

        {canManage && (
          <div className="d-flex justify-content-end mb-3">
            <AppButton size="sm" onClick={openCreate}>
              <Plus size={15} className="me-1" /> Add category
            </AppButton>
          </div>
        )}

        {/* Sorting on, filters off: this is a modal holding an institute's own
            short list of categories, not a list to interrogate. `AppDataTable`
            owns the loading shimmer and the empty state, so both branches the
            old markup carried are gone. */}
        <AppDataTable
          fill={false}
          columns={columns}
          rows={rows}
          rowKey={(row) => row._id}
          loading={loading}
          sort={sort}
          onSortChange={(key, direction) => setSort({ key, direction })}
          emptyMessage="No categories yet. Add one to organize courses on your marketing site."
        />

        <div className="d-flex justify-content-end mt-3">
          <AppButton variant="secondary" onClick={onHide}>
            Close
          </AppButton>
        </div>
      </AppModal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      {editing && (
        <AppModal show title={editing._id ? 'Edit category' : 'New category'} onHide={close} size="md">
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={editing.label}
              onChange={(e) => setEditing((ed) => ({ ...ed, label: e.target.value }))}
              placeholder="e.g. Data Science"
              autoFocus
            />
          </Form.Group>
          <Form.Check
            type="switch"
            id="cat-active-modal"
            label="Show on public website"
            checked={!!editing.isActive}
            onChange={(e) => setEditing((ed) => ({ ...ed, isActive: e.target.checked }))}
          />
          {/* ⚠️ Prefixed, because this file now carries `tw-` classes and
              `gap-2` is one of the names Bootstrap and Tailwind both emit —
              `lint:tokens --check prefix` fails on the ambiguity. The values
              happen to agree here (both 0.5rem), but `mt-4` does NOT: Bootstrap's
              is 1.5rem, so it becomes `tw-mt-6`, not `tw-mt-4`. */}
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-6">
            <AppButton variant="ghost" onClick={close}>
              Cancel
            </AppButton>
            <AppButton onClick={save} disabled={saving}>
              {saving ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <>
                  <Save size={15} className="me-1" /> Save
                </>
              )}
            </AppButton>
          </div>
        </AppModal>
      )}

      {deleteId && (
        <AppModal
          show
          title="Delete category?"
          onHide={() => setDeleteId(null)}
          footer={
            <>
              <AppButton variant="ghost" onClick={() => setDeleteId(null)}>
                Cancel
              </AppButton>
              <AppButton variant="destructive" onClick={confirmDelete}>
                Delete
              </AppButton>
            </>
          }
        >
          <p className="mb-0">Only allowed when no course uses this category.</p>
        </AppModal>
      )}
    </>
  );
}
